/* eslint-disable max-lines-per-function */
import { Typography } from "frontend/src/components/Typography/Typography";
import { Menu } from "frontend/src/components/Menu/Menu";
import { Checkbox } from "frontend/src/components/Checkbox/Checkbox";
import {
  Box,
  Button,
  Divider,
  Link as MuiLink,
  Stack,
  Typography as MuiTypography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import Modal from "frontend/src/components/Modal/Modal";
import React, { Dispatch, SetStateAction } from "react";
import * as types from "_graphql-types/graphql";

import { useOptionalCurrentUser } from "frontend/src/utils/hooks";
import { useMutation, useQuery } from "@apollo/client";
import {
  SAVED_ENTITY_SEARCH_QUERY,
  INVESTMENT_SET_QUERY,
  CLEAR_USER_SETTING,
} from "./graphql";

interface ModalState {
  open: boolean;
  saveError: string;
}

type Props = {
  currentUser: types.CurrentUserQuery["currentUser"];
  modalState: ModalState;
  setModalState: Dispatch<SetStateAction<ModalState>>;
};

const initModal = (): ModalState => ({
  open: false,
  saveError: "",
});

export const UserSettingsModal = ({
  currentUser,
  modalState,
  setModalState,
  bodyOnly = false,
}: Props & { bodyOnly?: boolean }) => {
  const { userSettings } = currentUser;

  const [clearUserSetting] = useMutation(CLEAR_USER_SETTING);

  const { data: savedEntitySearchData } = useQuery<{
    savedEntitySearch: types.SavedEntitySearchQuery["savedEntitySearch"];
  }>(SAVED_ENTITY_SEARCH_QUERY, {
    variables: {
      id: userSettings?.data?.defaultInvestmentView?.id,
    },
    skip:
      userSettings?.data?.defaultInvestmentView?.type !==
      types.SettingEntityType.SavedEntitySearch,
  });

  const { data: investmentSetData } = useQuery<{
    investmentSet: types.InvestmentSetSearchQuery["investmentSet"];
  }>(INVESTMENT_SET_QUERY, {
    variables: {
      id: userSettings?.data?.defaultInvestmentView?.id,
    },
    skip:
      userSettings?.data?.defaultInvestmentView?.type !==
      types.SettingEntityType.InvestmentSet,
  });

  function clearDefaultView() {
    clearUserSetting({
      variables: {
        input: {
          clearSettingKey: types.ClearSettingKey.DefaultInvestmentView,
          userId: currentUser.id,
        },
      },
    });
  }

  function renderSetting(searchSetting?: types.SearchSetting | null) {
    if (!searchSetting) {
      return (
        <MuiTypography variant="body2" color="text.secondary">
          No default view yet. Set one by editing a list, or by saving a filter.
        </MuiTypography>
      );
    }

    const typeLabel =
      userSettings?.data?.defaultInvestmentView?.type &&
      I18n.t(
        `nav_bar.user_settings.${userSettings?.data?.defaultInvestmentView?.type}`
      );
    const name =
      searchSetting.type === types.SettingEntityType.InvestmentSet
        ? investmentSetData?.investmentSet?.name
        : savedEntitySearchData?.savedEntitySearch?.name;

    // Label above value rather than a two-column grid: the values are short and
    // the grid left a wide empty gutter on a full-width page.
    return (
      <Stack direction="row" spacing={6}>
        <Box>
          <MuiTypography variant="caption" color="text.secondary" display="block">
            Type
          </MuiTypography>
          <MuiTypography variant="body2" data-cy="userSettingsModal__type">
            {typeLabel}
          </MuiTypography>
        </Box>
        <Box>
          <MuiTypography variant="caption" color="text.secondary" display="block">
            Name
          </MuiTypography>
          <MuiTypography variant="body2" data-cy="userSettingsModal__name">
            {name}
          </MuiTypography>
        </Box>
      </Stack>
    );
  }

  const body = (
    <Box data-cy="userSettingsModal__body" sx={{ maxWidth: 640 }}>
      <MuiTypography variant="subtitle1">
        {I18n.t("nav_bar.user_settings.defaultInvestmentView")}
      </MuiTypography>
      <MuiTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The list or saved filter Sirius opens on when you go to Investments.
      </MuiTypography>

      <Box sx={{ mb: 2 }}>
        {renderSetting(userSettings?.data?.defaultInvestmentView)}
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      <Stack direction="row" spacing={2} alignItems="center">
        {/* An action that takes effect immediately, so a button rather than a
            checkbox — ticking a box and having it apply with no confirmation read
            as a setting you could untick. */}
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          data-cy="userSettingsModal__clearSettings"
          disabled={!userSettings?.data?.defaultInvestmentView}
          onClick={() =>
            userSettings?.data?.defaultInvestmentView &&
            clearDefaultView()
          }
        >
          Clear default view
        </Button>

        {/* Where you actually set one — the copy above says "edit a list or save a
            filter", which is not obviously a place you can go. */}
        <MuiLink
          component={RouterLink}
          to="/investments"
          variant="body2"
          data-cy="userSettingsModal__investmentsLink"
        >
          Go to Investments
        </MuiLink>
      </Stack>
    </Box>
  );

  // Rendered without modal chrome when the settings page hosts it (bodyOnly), and
  // inside the dialog for the navbar flow. Same markup and the same data-cy hooks
  // either way, so the existing e2e selectors keep working.
  if (bodyOnly) return body;

  return (
    <Modal
      data-cy="userSettings__modal"
      title="User Settings"
      okText="Save"
      open={modalState.open}
      onOk={async () => {
        setModalState({
          ...modalState,
          open: false,
        });
      }}
      onCancel={() => setModalState(initModal())}
    >
      {body}
    </Modal>
  );
};

// The menu item lives INSIDE the navbar Dropdown overlay and only toggles open
// state. The modal itself is rendered by <UserSettingsModalHost> OUTSIDE the
// Dropdown (see navbar.tsx). This separation is essential: the modal is a portal
// outside the dropdown's DOM, so a click inside it (e.g. the "Clear Settings"
// checkbox) counts as a click-outside and the Dropdown's clickaway would close the
// overlay — unmounting the modal mid-interaction (the whole modal vanished).
export const UserSettingsMenuItem = ({
  onOpen,
}: {
  onOpen: () => void;
}): JSX.Element => (
  <Menu.Item data-cy="main-navbar__userSettings" onClick={onOpen}>
    <p className="main-popup-dropdown__item">User Settings</p>
  </Menu.Item>
);

// Rendered at the navbar level, OUTSIDE the Dropdown, so its lifecycle is
// independent of the dropdown's open state. Uses the optional current-user hook and
// guards on null so a transient auth-cache miss can't throw and unmount the modal.
export const UserSettingsModalHost = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element | null => {
  const currentUser = useOptionalCurrentUser();
  if (!currentUser) return null;

  return (
    <UserSettingsModal
      currentUser={currentUser}
      modalState={{ open, saveError: "" }}
      // UserSettingsModal only ever sets open:false (onOk/onCancel); treat any such
      // state change as a request to close.
      setModalState={next => {
        const resolved =
          typeof next === "function" ? next({ open, saveError: "" }) : next;
        if (!resolved.open) onClose();
      }}
    />
  );
};

/**
 * The same settings content without dialog chrome, for the settings page.
 *
 * Shares one implementation with the navbar modal deliberately: two copies would
 * drift, and the e2e selectors (userSettingsModal__*) are asserted against this
 * markup.
 */
export const UserSettingsPanel = (): JSX.Element | null => {
  const currentUser = useOptionalCurrentUser();
  if (!currentUser) return null;
  return (
    <UserSettingsModal
      currentUser={currentUser}
      modalState={{ open: true, saveError: "" }}
      setModalState={() => undefined}
      bodyOnly
    />
  );
};
